import { formatCurrency } from './utils/formatters.js'
import type { StatCard } from './cards'

/**
 * How a card's number is written, and what the card means — pure (Phase 1I).
 *
 * Ported from `utils/cardHelpers.js` without its ref-unwrapping branch:
 * `buildCards` returns plain numbers, so there is nothing to unwrap. (The Vue
 * version needed it because two cards defined `value` as a nested `computed()`,
 * which is what made Total Demand print unformatted.)
 */

/**
 * How currency is written, as the Customize Map modal's radio offers it.
 *
 * `abbreviated` is the default and is exactly what the bar has always
 * rendered — `formatCurrency`, unchanged. The other two existed only in a
 * rival formatter used by the settings preview, so choosing them changed the
 * preview and nothing else; they reach the map now.
 */
export type NumberFormat = 'abbreviated' | 'full' | 'thousands'

function currency(value: number, format: NumberFormat): string {
  if (format === 'full') {
    return `${value < 0 ? '-' : ''}$${Math.abs(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }
  if (format === 'thousands') {
    return `${value < 0 ? '-' : ''}$${Math.round(Math.abs(value) / 1000).toLocaleString('en-US')} K`
  }
  return formatCurrency(value)
}

/**
 * A card with no value renders an em dash — never a blank, and never zero.
 *
 * Cost of Averaging is the case that forced this: zero would assert that
 * uncertainty is free, when in fact no design run has happened.
 */
export function formatCardValue(
  card: Pick<StatCard, 'value' | 'type'>,
  numberFormat: NumberFormat = 'abbreviated',
): string {
  const raw = card.value
  if (raw === null || raw === undefined) return '—'
  if (!Number.isFinite(raw)) return '—'

  switch (card.type) {
    case 'currency':
      return currency(raw, numberFormat)
    case 'percent':
      return `${raw.toFixed(1)}%`
    case 'number':
      // Thousands separators, not compact K/M. These are unit counts, and
      // "55,798" is the figure people reconcile against their own data.
      return Math.round(raw).toLocaleString('en-US')
    case 'risk':
      return `${raw.toFixed(1)}/5.0`
    default:
      return String(raw)
  }
}

const DESCRIPTIONS: Record<string, string> = {
  totalCost: 'Total network cost including fixed facility costs, variable operating costs, and transportation costs.',
  fixedCost: 'Fixed costs associated with operating distribution centers and facilities.',
  transportCost: 'Transportation and shipping costs for moving goods through the network.',
  variableCost: 'Variable operating costs that scale with volume (e.g., labor, utilities, handling).',
  holdingCost: 'Inventory holding cost — the capital and storage cost of the safety stock the network carries.',
  capacityUtil: 'Average capacity utilization across all facilities. Higher values indicate more efficient use of existing capacity.',
  totalFacilities: 'Total number of facilities in the network including DCs, warehouses, and distribution points.',
  serviceLevel: 'Percentage of customer demand met within service level requirements (distance/time).',
  totalCustomers: 'Total number of customer locations served by the network.',
  totalDemand: 'Total demand (in units) across all customers. Shows fulfillment percentage in comparison mode.',
  spareCapacity: 'Total unused capacity available across all facilities. Useful for handling demand spikes or seasonal peaks.',
  valueOfStochasticSolution:
    'What optimizing for average demand costs you, measured against optimizing under uncertainty. Produced by a design-under-uncertainty run.',
}

export function cardDescription(cardId: string): string {
  return DESCRIPTIONS[cardId] || 'No description available.'
}

/**
 * A card's `color` as something that can actually be painted.
 *
 * The catalogue stores VUETIFY colour names, and only some of them are also CSS
 * colours: `green`, `orange`, `purple`, `brown` and `blue` are, while `primary`,
 * `success`, `info`, `error`, `warning` and `deep-purple` are not. Handing the
 * latter to `backgroundColor` yields a declaration the browser drops — which is
 * how the detail dialog once painted white text on white, and how the chart for
 * Open DCs drew its bar in the default black.
 *
 * Theme tokens are read from Vuetify's CSS variables so the dark theme is
 * honoured; the hexes are the light theme's values, used when there is no DOM
 * (tests) or the variable is missing.
 */
const THEME_FALLBACKS: Record<string, string> = {
  primary: '#0066CC',
  secondary: '#00a3ff',
  success: '#0066CC',
  info: '#0066CC',
  warning: '#FB8C00',
  error: '#CF6679',
}

const MATERIAL: Record<string, string> = {
  'deep-purple': '#673AB7',
}

export function resolveCardColor(color: string | undefined | null): string {
  if (!color) return THEME_FALLBACKS.primary
  if (MATERIAL[color]) return MATERIAL[color]
  if (!(color in THEME_FALLBACKS)) return color

  try {
    const channels = getComputedStyle(document.documentElement)
      .getPropertyValue(`--v-theme-${color}`)
      .trim()
    // Vuetify stores the channels bare ("0,102,204") so they can be composed
    // with an alpha; an empty string means the theme has not been applied here.
    if (channels) return `rgb(${channels})`
  } catch {
    // No DOM, or a browser that refuses the read. The fallback is correct for
    // the light theme, which is the default.
  }
  return THEME_FALLBACKS[color]
}
